import React, { useCallback, useEffect, useState } from 'react';
import { CalendarDays } from 'lucide-react';
import { useOutletContext } from 'react-router-dom';
import { useSuperadminAuth } from '../../context/SuperadminAuthContext';
import {
  DataListTable,
  ErrorState,
  LoadingState,
  PaginationControls,
  SectionCard,
  StatusBadge,
  formatDateTime,
} from '../../components/superadmin/SuperAdminUI';

const DEFAULT_PAGINATION = { page: 1, pageSize: 20, total: 0, totalPages: 0 };

const ShopBookings = () => {
  const { shop, shopId } = useOutletContext();
  const { getShopBookings } = useSuperadminAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [bookings, setBookings] = useState([]);
  const [pagination, setPagination] = useState(DEFAULT_PAGINATION);

  const loadBookings = useCallback(async (page = 1) => {
    try {
      setLoading(true);
      setError('');
      const response = await getShopBookings(shopId, {
        page,
        pageSize: pagination.pageSize,
        sort: 'start_time',
        order: 'desc',
      });

      if (!response?.ok) {
        throw new Error('Failed to load bookings');
      }

      setBookings(response.data || []);
      setPagination(response.pagination || DEFAULT_PAGINATION);
    } catch (err) {
      setError(err?.response?.data?.message || err.message || 'Failed to load bookings');
    } finally {
      setLoading(false);
    }
  }, [getShopBookings, pagination.pageSize, shopId]);

  useEffect(() => {
    loadBookings(1);
  }, [loadBookings]);

  return (
    <div className="space-y-6">
      {error ? <ErrorState error={error} onRetry={() => loadBookings(pagination.page)} /> : null}

      <SectionCard
        title="Bookings"
        subtitle={`Latest bookings for ${shop?.name || `Shop #${shopId}`}`}
        icon={<CalendarDays className="h-5 w-5" style={{ color: '#7d6633' }} />}
      >
        <div className="space-y-4">
          {loading ? (
            <LoadingState lines={6} />
          ) : (
            <DataListTable
              columns={[
                { key: 'service', label: 'Service', render: (row) => row.service?.name || 'N/A' },
                { key: 'client', label: 'Client', render: (row) => row.client?.name || 'N/A' },
                { key: 'barber', label: 'Barber', render: (row) => row.barber?.name || 'N/A' },
                {
                  key: 'status',
                  label: 'Status',
                  render: (row) => (
                    <StatusBadge
                      status={row.status}
                      variant={row.status === 'completed' ? 'success' : row.status === 'cancelled' ? 'danger' : 'warning'}
                    />
                  ),
                },
                { key: 'start_time', label: 'Start', render: (row) => formatDateTime(row.start_time) },
              ]}
              rows={bookings}
              emptyTitle="No bookings found"
              emptyDescription="No booking records were returned for this shop."
            />
          )}

          <PaginationControls pagination={pagination} onPageChange={loadBookings} loading={loading} />
        </div>
      </SectionCard>
    </div>
  );
};

export default ShopBookings;
